import type { Charger, ChargerStatus, ConnectorType, SearchHit } from '@/types/charger';
import type { FeatureCollection, Point } from 'geojson';

export { fetchVehicleProfile, saveVehicleProfile, type VehicleProfile } from './vehicle-profile';

export type ChargerProps = {
  id: number;
  name: string;
  operator: string;
  operatorCode: string;
  maxKw: number;
  power: 'ac' | 'dc';
  connectors: ConnectorType[];
  status: ChargerStatus;
  live: boolean;
  hasRealStatus?: boolean;
  evseCount?: number;
  evseAvailable?: number;
  evseOccupied?: number;
  priceKwh: number | null;
  settlement: string;
  street: string;
  postalCode: number | null;
  source: string;
  openingHours?: string | null;
  usageCost?: string | null;
  networkNotice?: string | null;
  crowdAt?: string | null;
  wcGas5Min?: boolean;
  cpoGroup?: string;
  occupancyRate?: number;
};

export type ChargerFC = FeatureCollection<Point, ChargerProps>;

function parseConnectors(raw: unknown): ConnectorType[] {
  let list: unknown = raw;
  if (typeof raw === 'string') {
    try {
      list = JSON.parse(raw);
    } catch {
      list = raw ? [raw] : [];
    }
  }
  if (!Array.isArray(list)) return [];
  const allowed: ConnectorType[] = ['Type2', 'CCS', 'CHAdeMO'];
  return list.filter((x): x is ConnectorType => allowed.includes(x as ConnectorType));
}

export function featureToCharger(f: ChargerFC['features'][number]): Charger {
  const p = f.properties ?? ({} as ChargerProps);
  const coords = f.geometry?.coordinates;
  const lng = Array.isArray(coords) ? Number(coords[0]) : 0;
  const lat = Array.isArray(coords) ? Number(coords[1]) : 0;
  const liveRaw: unknown = p.live;
  const live = liveRaw === true || liveRaw === 1 || String(liveRaw) === 'true';
  return {
    id: Number(p.id),
    name: String(p.name ?? ''),
    operator: String(p.operator ?? ''),
    operatorCode: String(p.operatorCode ?? ''),
    maxKw: Number(p.maxKw) || 0,
    status: (p.status as ChargerStatus) || 'unknown',
    connectors: parseConnectors(p.connectors),
    power:
      String(p.power) === 'dc' ||
      parseConnectors(p.connectors).some((c) => c === 'CCS' || c === 'CHAdeMO')
        ? 'dc'
        : 'ac',
    coordinates: [lng, lat],
    live: Boolean(live),
    evseCount: Number(p.evseCount) || 0,
    evseAvailable: Number(p.evseAvailable) || 0,
    evseOccupied: Number(p.evseOccupied) || 0,
    priceKwh: p.priceKwh == null || Number.isNaN(Number(p.priceKwh)) ? null : Number(p.priceKwh),
    settlement: String(p.settlement ?? ''),
    street: String(p.street ?? ''),
    postalCode: p.postalCode == null ? null : Number(p.postalCode),
    source: String(p.source ?? ''),
    openingHours: p.openingHours ? String(p.openingHours) : null,
    usageCost: p.usageCost ? String(p.usageCost) : null,
    crowdAt: p.crowdAt ? String(p.crowdAt) : null,
  };
}

export async function fetchGeojson(): Promise<ChargerFC> {
  const res = await fetch('/v1/chargers/geojson');
  if (!res.ok) throw new Error('geojson failed');
  return res.json();
}

export async function fetchStats(): Promise<{ chargers_active: number }> {
  const res = await fetch('/v1/stats', { cache: 'no-store' });
  if (!res.ok) throw new Error('stats failed');
  const json = await res.json();
  return { chargers_active: Number(json?.data?.chargers_active ?? 0) };
}

export async function fetchOperators(): Promise<Array<{ organization_code: string; name: string }>> {
  const res = await fetch('/v1/operators', { cache: 'no-store' });
  if (!res.ok) return [];
  const json = await res.json();
  return json.data ?? [];
}

export async function searchQuery(q: string): Promise<SearchHit[]> {
  const res = await fetch('/v1/search?' + new URLSearchParams({ q }));
  if (!res.ok) return [];
  const json = await res.json();
  return json.data ?? [];
}

export async function fetchCharger(id: number) {
  const res = await fetch(`/v1/chargers/${id}`, { cache: 'no-store' });
  if (!res.ok) return null;
  const json = await res.json();
  return json.data ?? null;
}

export type StatusChange = { id: number; status: ChargerStatus; live: boolean;
  hasRealStatus?: boolean;
  evseCount?: number;
  evseAvailable?: number;
  evseOccupied?: number; updated_at?: string };

export async function fetchStatusChanges(since: string): Promise<{ ts: string; changes: StatusChange[] }> {
  const q = since ? '?' + new URLSearchParams({ since }) : '';
  const res = await fetch('/v1/chargers/status' + q, { cache: 'no-store' });
  if (!res.ok) return { ts: since, changes: [] };
  const json = await res.json();
  return { ts: String(json.ts ?? since), changes: json.changes ?? [] };
}

export type CheckinKind = 'available' | 'occupied' | 'broken' | 'charged';

export async function fetchProposals(): Promise<FeatureCollection> {
  const res = await fetch('/v1/proposals/geojson', { cache: 'no-store' });
  if (!res.ok) return { type: 'FeatureCollection', features: [] };
  return res.json();
}

export type GeocodeHit = {
  lat: number | null;
  lng: number | null;
  settlement: string;
  street: string;
  postalCode: number | null;
  display: string;
};

export async function geocodeReverse(lat: number, lng: number): Promise<GeocodeHit | null> {
  try {
    const res = await fetch('/v1/geocode?' + new URLSearchParams({ lat: String(lat), lng: String(lng) }), {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.data ?? null;
  } catch {
    return null;
  }
}

export async function geocodeForward(q: string): Promise<GeocodeHit | null> {
  const res = await fetch('/v1/geocode?' + new URLSearchParams({ q }), { cache: 'no-store' });
  if (!res.ok) return null;
  const json = await res.json();
  return json.data ?? null;
}

export type RoutePlanResult = {
  geometry: { type: 'LineString'; coordinates: [number, number][] } | null;
  distanceM: number;
  durationS: number;
  legs: Array<{ distanceM: number; durationS: number }>;
  waypoints: [number, number][];
};

/** Login-gated: the PHP backend proxies this to the internal self-hosted OSRM instance. */
export async function fetchRoute(
  waypoints: [number, number][]
): Promise<{ ok: boolean; data?: RoutePlanResult; error?: string }> {
  const q = waypoints.map(([lng, lat]) => `${lng},${lat}`).join(';');
  const res = await fetch('/v1/route?' + new URLSearchParams({ waypoints: q }), {
    credentials: 'include',
    cache: 'no-store',
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 401) return { ok: false, error: 'unauthenticated' };
  if (!res.ok) return { ok: false, error: String(json.error ?? 'failed') };
  return { ok: true, data: json.data };
}

/** Login-gated: saves the user's default route-planner network preferences. */
export async function setPreferredOperators(codes: string[]): Promise<{ ok: boolean; data?: AuthMe; error?: string }> {
  const res = await fetch('/v1/account/preferred-operator', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ operator_codes: codes }),
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 401) return { ok: false, error: 'unauthenticated' };
  if (!res.ok) return { ok: false, error: String(json.error ?? 'failed') };
  return { ok: true, data: json.data };
}

export async function createProposal(body: Record<string, unknown>): Promise<{ ok: boolean; data?: { id: number }; error?: string }> {
  const res = await fetch('/v1/proposals', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 401) return { ok: false, error: 'unauthenticated' };
  if (!res.ok) return { ok: false, error: String(json.error ?? 'failed') };
  return { ok: true, data: json.data };
}

export async function confirmProposal(id: number): Promise<{ ok: boolean; error?: string; data?: Record<string, unknown> }> {
  const res = await fetch(`/v1/proposals/${id}/confirm`, { method: 'POST', credentials: 'include' });
  const json = await res.json().catch(() => ({}));
  if (res.status === 401) return { ok: false, error: 'unauthenticated' };
  if (!res.ok) return { ok: false, error: String(json.error ?? 'failed') };
  return { ok: true, data: json.data };
}

export async function fetchProposal(id: number) {
  const res = await fetch(`/v1/proposals/${id}`, { cache: 'no-store', credentials: 'include' });
  if (!res.ok) return null;
  const json = await res.json();
  return json.data ?? null;
}

export interface PriceSuggestionItem {
  label: string;
  kind?: string;
  price_kwh?: number | null;
  price_minute?: number | null;
  price_flat?: number | null;
  is_adhoc?: boolean;
}

export async function suggestPrice(
  id: number,
  priceKwhOrPayload: number | { tariffs: PriceSuggestionItem[]; comment?: string },
  priceMinute?: number | null,
  comment?: string
) {
  let body: Record<string, unknown>;
  if (typeof priceKwhOrPayload === 'object' && priceKwhOrPayload !== null) {
    body = priceKwhOrPayload;
  } else {
    body = {
      priceKwh: priceKwhOrPayload,
      priceMinute: priceMinute ?? null,
      comment: comment ?? '',
    };
  }
  const res = await fetch(`/v1/chargers/${id}/price`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 401) return { ok: false, error: 'unauthenticated' };
  if (!res.ok) return { ok: false, error: String(json.error ?? 'failed') };
  return { ok: true };
}

export async function uploadChargerPhotos(id: number, files: File[]) {
  const fd = new FormData();
  files.forEach((f) => fd.append('photos[]', f));
  const res = await fetch(`/v1/chargers/${id}/photos`, { method: 'POST', credentials: 'include', body: fd });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: String(json.error ?? 'failed') };
  return { ok: true, data: json.data as Array<{ url: string }> };
}

export async function uploadProposalPhotos(id: number, files: File[]) {
  const fd = new FormData();
  files.forEach((f) => fd.append('photos[]', f));
  const res = await fetch(`/v1/proposals/${id}/photos`, { method: 'POST', credentials: 'include', body: fd });
  return res.ok;
}

export type ChargingQuote = {
  startable?: boolean;
  plan_name?: string;
  energy_huf_kwh?: number | null;
  max_hold?: number;
  currency?: string;
  estimate?: { minutes: number; kwh: number; total_incl_vat: number };
};

export async function fetchChargingQuote(chargerId: number, evseUid?: string): Promise<ChargingQuote | null> {
  const q = new URLSearchParams({ charger_id: String(chargerId) });
  if (evseUid) q.set('evse_uid', evseUid);
  const res = await fetch('/v1/charging/quote?' + q, { credentials: 'include', cache: 'no-store' });
  if (!res.ok) return null;
  const json = (await res.json().catch(() => ({}))) as { data?: ChargingQuote };
  return json.data ?? null;
}

export async function prepareOcpiCharge(
  chargerId: number,
  evseUid: string,
): Promise<{ ok: boolean; error?: string; message?: string; data?: Record<string, unknown> }> {
  const res = await fetch('/v1/charging/prepare', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ charger_id: chargerId, evse_uid: evseUid }),
  });
  const json = (await res.json().catch(() => ({}))) as { error?: string; message?: string; data?: Record<string, unknown> };
  if (res.status === 401) return { ok: false, error: 'unauthenticated' };
  if (!res.ok) return { ok: false, error: String(json.error ?? 'failed'), message: json.message };
  return { ok: true, data: json.data };
}

export async function ocpiPaymentStatus(
  sessionToken: string,
  chargerId?: number,
): Promise<{
  ok: boolean;
  confirmed?: boolean;
  message?: string;
  charger_id?: number;
  evse_uid?: string;
}> {
  const q = new URLSearchParams({ session_token: sessionToken });
  if (chargerId) q.set('charger_id', String(chargerId));
  const res = await fetch('/v1/charging/payment-status?' + q, {
    credentials: 'include',
    cache: 'no-store',
  });
  const json = (await res.json().catch(() => ({}))) as {
    data?: { confirmed?: boolean; charger_id?: number; evse_uid?: string };
    message?: string;
  };
  if (!res.ok) return { ok: false, message: json.message };
  return {
    ok: true,
    confirmed: Boolean(json.data?.confirmed),
    charger_id: json.data?.charger_id,
    evse_uid: json.data?.evse_uid,
  };
}

export async function startOcpiCharge(
  chargerId: number,
  evseUid?: string,
  sessionToken?: string,
): Promise<{ ok: boolean; error?: string; message?: string; data?: Record<string, unknown> }> {
  const res = await fetch('/v1/charging/start', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ charger_id: chargerId, evse_uid: evseUid || undefined, session_token: sessionToken || undefined }),
  });
  const json = (await res.json().catch(() => ({}))) as { error?: string; message?: string; data?: Record<string, unknown> };
  if (res.status === 401) return { ok: false, error: 'unauthenticated' };
  if (!res.ok) return { ok: false, error: String(json.error ?? 'failed'), message: json.message };
  return { ok: true, data: json.data };
}

export async function stopOcpiCharge(
  sessionId?: string,
): Promise<{ ok: boolean; error?: string; message?: string; data?: Record<string, unknown> }> {
  const res = await fetch('/v1/charging/stop', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId || undefined }),
  });
  const json = (await res.json().catch(() => ({}))) as { error?: string; message?: string; data?: Record<string, unknown> };
  if (res.status === 401) return { ok: false, error: 'unauthenticated' };
  if (!res.ok) return { ok: false, error: String(json.error ?? 'failed'), message: json.message };
  return { ok: true, data: json.data };
}

export type LiveCharge = {
  id: string | null;
  charger_id: number | null;
  evse_uid: string | null;
  status: string;
  phase: string;
  kwh: number;
  total_cost: number | null;
  currency: string;
  start_datetime: string | null;
  end_datetime?: string | null;
  last_updated: string | null;
  label: string;
  can_stop: boolean;
  can_close?: boolean;
  wait_hint?: string | null;
};

export async function fetchChargingLive(): Promise<LiveCharge | null> {
  const res = await fetch('/v1/charging/live', { credentials: 'include', cache: 'no-store' });
  if (!res.ok) return null;
  const json = (await res.json().catch(() => ({}))) as { data?: LiveCharge | null };
  return json.data ?? null;
}

export async function fetchChargingSessions(): Promise<Array<Record<string, unknown>>> {
  const res = await fetch('/v1/charging/sessions', { credentials: 'include', cache: 'no-store' });
  if (!res.ok) return [];
  const json = (await res.json()) as { data?: Array<Record<string, unknown>> };
  return Array.isArray(json.data) ? json.data : [];
}

export async function postCheckin(id: number, kind: CheckinKind): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch(`/v1/chargers/${id}/checkin`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind }),
  });
  if (res.status === 401) return { ok: false, error: 'unauthenticated' };
  if (res.status === 429) return { ok: false, error: 'too_soon' };
  if (!res.ok) return { ok: false, error: 'failed' };
  return { ok: true };
}


export type PoiCategory = "restaurant" | "cafe" | "fast_food" | "toilet" | "gas_station" | "shop" | "hotel" | "other";

export type PoiItem = {
  id: string;
  name: string;
  category: PoiCategory;
  category_label: string;
  category_icon: string;
  types: string[];
  address: string;
  lat: number;
  lng: number;
  distance_m: number;
  walk_min: number;
  walk_zone: "1_min" | "5_min" | "10_min" | "15_min";
  rating: number | null;
  user_ratings_total: number | null;
  price_level: number | null;
  open_now: boolean | null;
  business_status: string;
  photos: Array<{ url: string; width: number; height: number }>;
  cover_photo: string | null;
  google_maps_url: string;
};

export type PoiResponse = {
  center: { lat: number; lng: number };
  radius_m: number;
  zones: Record<string, { min: number; distance_m: number; label: string; color: string }>;
  categories_summary: Record<string, number>;
  total: number;
  poi_list: PoiItem[];
  charger?: {
    id: number;
    settlement: string;
    street: string;
    equipment_type: string;
    lat: number;
    lng: number;
  };
};

export async function fetchChargerPoi(
  chargerId: number,
  category?: string,
  radiusM: number = 1500
): Promise<PoiResponse | null> {
  try {
    const params = new URLSearchParams();
    if (radiusM) params.set("radius", String(radiusM));
    if (category && category !== "all") params.set("category", category);
    const res = await fetch(`/v1/chargers/${chargerId}/poi?` + params.toString(), {
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) return null;
    const json = (await res.json().catch(() => null)) as { data?: PoiResponse };
    return json?.data ?? null;
  } catch {
    return null;
  }
}

export async function fetchPoiDetails(placeId: string): Promise<any> {
  try {
    const res = await fetch(`/v1/poi/details?place_id=${encodeURIComponent(placeId)}`, {
      credentials: "include",
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json?.data || null;
  } catch (err) {
    console.warn("fetchPoiDetails failed", err);
    return null;
  }
}

export type AuthMe = {
  id?: number;
  name?: string;
  email?: string;
  role?: string;
  preferredOperators: Array<{ code: string; name: string }>;
  teslaLinked?: boolean;
};

let authMePromise: Promise<AuthMe | null> | null = null;

export async function fetchAuthMe(): Promise<AuthMe | null> {
  if (!authMePromise) {
    authMePromise = fetch('/v1/auth/me', { credentials: 'include', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => (j?.data ? j.data : null))
      .catch(() => null);
  }
  return authMePromise;
}
